import { forwardRef, useEffect } from 'react'
import { Beta } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'

import { TrendingTile } from 'pages/index/components/Marketing/components/TrendingTile'
import { TRENDING as TRENDING_SLUG } from 'libs/utils/categories'
import { Pen5038TrendingTileWrapper } from 'experiments/pen-5038_v1_mega_content_picker'

import {
  VideoModalContextProvider,
  TileSkeleton,
  styles,
  type CourseTile,
} from 'components/GlobalCarousel'

import { ConcreteCategory } from './dataFetching'

import {
  trackCarouselClickNext,
  trackCarouselClickPrev,
  trackTileClick,
  trackSeeAllClick,
} from './events'

type InstructorCarouselProps = {
  loading: boolean
  category: ConcreteCategory | undefined
  courseTiles: CourseTile[]
  openNewPage?: boolean
}

export const CourseTileCarousel = ({
  loading,
  category,
  courseTiles,
  openNewPage,
}: InstructorCarouselProps) => {
  const { t } = useTranslation('@mc/next')

  const categorySlug = category?.slug ?? TRENDING_SLUG
  const heading = t(`home.catalogExplorer.subheadings.${categorySlug}`)
  const categoryName = category?.name ?? t('home.catalogExplorer.trending')
  const label = t('home.catalogExplorer.a11y.coursesLabel', {
    category: categoryName,
  })

  return (
    <Carousel
      slug={categorySlug}
      loading={loading}
      heading={heading}
      label={label}
      tiles={courseTiles}
      openNewPage={openNewPage}
    />
  )
}

type TrendingRowProps = {
  trending: CourseTile[]
}
export const TrendingRow = ({ trending }: TrendingRowProps) => {
  const { t } = useTranslation('@mc/next')

  return (
    <Carousel
      slug={TRENDING_SLUG}
      heading={t('home.catalogExplorer.trending')}
      label={t('home.catalogExplorer.a11y.trendingLabel')}
      tiles={trending}
    />
  )
}

type CarouselProps = {
  slug: string
  heading: string
  label: string
  tiles: CourseTile[]
  loading?: boolean
  openNewPage?: boolean
}
export const Carousel = ({
  slug,
  loading,
  heading,
  label,
  tiles,
  openNewPage,
}: CarouselProps) => (
  <VideoModalContextProvider>
    <Beta.CarouselModern
      title={
        <CarouselHeading text={heading} seeAllHref={paths.category(slug)} />
      }
      srLabel={label}
      className={styles.carousel}
      arrowCls='d-none d-sm-block'
      pagesCls='d-none d-sm-block'
      onClickNext={() => trackCarouselClickNext(slug)}
      onClickPrev={() => trackCarouselClickPrev(slug)}
      overflow
    >
      <CarouselHook slug={slug} />
      {loading
        ? [1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={styles.tile}>
              <TileSkeleton />
            </div>
          ))
        : tiles.map((tile, index) => (
            <div key={tile.slug} className={styles.tile}>
              <Pen5038TrendingTileWrapper
                control={
                  <TrendingTile
                    tile={tile}
                    onClick={trackTileClick({
                      className: tile.title,
                      category: slug,
                    })}
                    openNewPage={openNewPage}
                    isJustLaunched={slug === TRENDING_SLUG && index === 0}
                  />
                }
                tile={tile}
                openNewPage={openNewPage}
                index={index}
                slug={slug}
              />
            </div>
          ))}
    </Beta.CarouselModern>
  </VideoModalContextProvider>
)

const CarouselResetPage = (
  { slug }: Pick<CarouselProps, 'slug'>,
  _: React.Ref<unknown>,
) => {
  const { container } = Beta.useCarousel()

  useEffect(() => {
    // go back to first page when the slug changes
    container?.scroll({
      left: 0,
      behavior: 'auto',
    })
  }, [slug, container])

  return null
}

export const CarouselHook = forwardRef(CarouselResetPage)
CarouselHook.displayName = 'CarouselHook'

const CarouselHeading = ({
  text,
  seeAllHref,
}: {
  text: string
  seeAllHref?: string
}) => {
  const { t } = useTranslation('@mc/next')

  return (
    <>
      <h3 className='mc-text-h6 d-inline mc-mr-3'>{text}</h3>
      {seeAllHref && (
        <a
          className='mc-text--normal mc-opacity--muted'
          href={seeAllHref}
          target='_blank'
          rel='noreferrer'
          onClick={trackSeeAllClick}
        >
          {t('home.catalogExplorer.seeAll')}
        </a>
      )}
    </>
  )
}
